import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import type { Bot, SearchHit, SearchHitKind } from "@rakazo/contracts";
import { searchHitListKey } from "@rakazo/core";
import {
  Badge,
  BotAvatar,
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
  Kbd,
} from "@rakazo/ui-web";
import { FileText, Link2, MessageSquare, Repeat } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

/** Delay before the palette asks the server, so typing does not fire one request per key. */
export const PALETTE_SEARCH_DEBOUNCE_MS = 200;

/** Order and labels of the server hit groups shown under the matching chats. */
const HIT_GROUPS: readonly { kinds: readonly SearchHitKind[]; heading: () => string }[] = [
  { kinds: ["conversation", "message"], heading: () => t`Messages` },
  { kinds: ["file"], heading: () => t`Files` },
  { kinds: ["routine"], heading: () => t`Routines` },
  { kinds: ["link"], heading: () => t`Links` },
];

function HitIcon({ kind }: { kind: SearchHitKind }) {
  const props = { size: 16, strokeWidth: 1.8, "aria-hidden": true } as const;
  if (kind === "file") return <FileText {...props} />;
  if (kind === "routine") return <Repeat {...props} />;
  if (kind === "link") return <Link2 {...props} />;
  return <MessageSquare {...props} />;
}

function isApplePlatform() {
  if (typeof navigator === "undefined") return false;
  // platform is deprecated but still the most reliable Apple check in browsers.
  return /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent);
}

function botSearchText(bot: Bot) {
  return `${bot.name} ${bot.title} ${bot.description} ${bot.preview}`.toLowerCase();
}

function botSubtitle(bot: Bot) {
  return bot.description.trim() || bot.preview.trim() || bot.title.trim();
}

function botTitleTag(bot: Bot) {
  const title = bot.title.trim();
  if (!title) return null;
  // Title is already the subtitle when there is no description/preview.
  if (!(bot.description.trim() || bot.preview.trim())) return null;
  return title;
}

/** Ctrl/⌘+K overlay: jumps to a bot by name and lists server search hits (messages, files, routines) for keyboard navigation. */
export function CommandPalette({
  open,
  onOpenChange,
  bots,
  onSelectBot,
  search: searchSpace,
  onSelectHit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bots: Bot[];
  onSelectBot: (botId: string) => void;
  /** Server search (search.query); the palette never keeps its own copy of message text. */
  search?: (q: string, signal: AbortSignal) => Promise<SearchHit[]>;
  onSelectHit?: (hit: SearchHit) => void;
}) {
  const [search, setSearch] = useState("");
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [hitsLoading, setHitsLoading] = useState(false);
  const [modKey, setModKey] = useState("⌘");

  useEffect(() => {
    setModKey(isApplePlatform() ? "⌘" : "Ctrl");
  }, []);

  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  const filteredBots = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return bots;
    return bots.filter((bot) => botSearchText(bot).includes(needle));
  }, [bots, search]);

  const needle = search.trim();
  useEffect(() => {
    if (!open || !needle || !searchSpace) {
      setHits([]);
      setHitsLoading(false);
      return;
    }
    const abort = new AbortController();
    setHitsLoading(true);
    const timer = window.setTimeout(() => {
      searchSpace(needle, abort.signal)
        .then((next) => {
          if (!abort.signal.aborted) setHits(next);
        })
        .catch(() => {
          if (!abort.signal.aborted) setHits([]);
        })
        .finally(() => {
          if (!abort.signal.aborted) setHitsLoading(false);
        });
    }, PALETTE_SEARCH_DEBOUNCE_MS);
    return () => {
      abort.abort();
      window.clearTimeout(timer);
    };
  }, [needle, open, searchSpace]);

  useEffect(() => {
    if (!open) return;
    function onKey(event: KeyboardEvent) {
      if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey || event.repeat) {
        return;
      }
      if (!/^[1-9]$/.test(event.key)) return;
      const bot = filteredBots[Number(event.key) - 1];
      if (!bot) return;
      event.preventDefault();
      onSelectBot(bot.id);
      onOpenChange(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [filteredBots, onOpenChange, onSelectBot, open]);

  return (
    <CommandDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t`Search`}
      description={t`Search chats, messages, files and routines`}
      className="sm:max-w-xl"
    >
      <Command
        shouldFilter={false}
        className="rounded-xl! bg-popover"
        data-testid="command-palette"
      >
        <CommandInput
          value={search}
          onValueChange={setSearch}
          placeholder={t`Search chats, messages, files and routines`}
          autoComplete="off"
          name="command-palette-search"
          data-testid="command-palette-search"
        />
        <CommandList className="max-h-80" data-testid="command-palette-list">
          <CommandEmpty>
            {hitsLoading ? <Trans>Searching…</Trans> : <Trans>No results</Trans>}
          </CommandEmpty>
          {filteredBots.length > 0 ? (
            <CommandGroup heading={needle ? t`Chats` : undefined}>
              {filteredBots.map((bot, index) => {
                const subtitle = botSubtitle(bot);
                const titleTag = botTitleTag(bot);
                const shortcut = index < 9 ? `${modKey}${index + 1}` : null;
                return (
                  <CommandItem
                    key={bot.id}
                    value={bot.id}
                    data-testid={`command-palette-bot-${bot.id}`}
                    onSelect={() => {
                      onSelectBot(bot.id);
                      onOpenChange(false);
                    }}
                    className="items-center gap-3 rounded-xl! px-2.5 py-2.5"
                  >
                    <BotAvatar color={bot.color} identity={bot.id} size={32} status={bot.status} />
                    <div className="min-w-0 flex-1">
                      <div className="flex min-w-0 items-center gap-2">
                        <span className="truncate font-medium text-foreground" dir="auto">
                          {bot.name}
                        </span>
                        {titleTag ? (
                          <Badge
                            variant="secondary"
                            className="max-w-[40%] truncate rounded-full px-1.5 py-0 text-[11px] font-normal text-muted-foreground"
                          >
                            {titleTag}
                          </Badge>
                        ) : null}
                      </div>
                      {subtitle ? (
                        <div
                          className="mt-0.5 truncate text-[13px] text-muted-foreground"
                          dir="auto"
                        >
                          {subtitle}
                        </div>
                      ) : null}
                    </div>
                    {shortcut ? (
                      <CommandShortcut className="tracking-normal">
                        <Kbd>{shortcut}</Kbd>
                      </CommandShortcut>
                    ) : null}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          ) : null}
          {HIT_GROUPS.map((group) => {
            const groupHits = hits.filter((hit) => group.kinds.includes(hit.kind));
            if (groupHits.length === 0) return null;
            return (
              <CommandGroup key={group.kinds.join("-")} heading={group.heading()}>
                {groupHits.map((hit) => (
                  <CommandItem
                    key={searchHitListKey(hit)}
                    value={`hit:${searchHitListKey(hit)}`}
                    data-testid="command-palette-hit"
                    onSelect={() => {
                      onSelectHit?.(hit);
                      onOpenChange(false);
                    }}
                    className="items-start gap-3 rounded-xl! px-2.5 py-2"
                  >
                    <span className="mt-0.5 text-muted-foreground">
                      <HitIcon kind={hit.kind} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium text-foreground" dir="auto">
                        {hit.title}
                      </div>
                      <div className="mt-0.5 flex min-w-0 gap-2 text-[13px] text-muted-foreground">
                        <span
                          className="max-w-[40%] shrink-0 truncate text-foreground/75"
                          dir="auto"
                        >
                          {hit.groupName ?? hit.botName}
                        </span>
                        <span className="truncate" dir="auto">
                          {hit.snippet}
                        </span>
                      </div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            );
          })}
        </CommandList>
      </Command>
    </CommandDialog>
  );
}

export { isCommandPaletteHotkey } from "./command-palette-hotkey";
